import React from 'react';
import { ArrowUpRight, Play } from 'lucide-react';
import { founder, clientVideo } from '../mock';
import Results from './Results';

const stats = [
  { value: '15+', label: 'Years Filming Stories' },
  { value: '200+', label: 'Productions Completed' },
  { value: 'Millions', label: 'Of Online Views' },
  { value: 'National', label: 'Broadcast Experience' },
  { value: 'Pro Sports', label: 'Partnerships' },
];

const About = ({ onPlay }) => {
  return (
    <section id="about" className="py-24 md:py-32 max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="absolute -inset-3 border border-[#c9a85f]/30 rounded-2xl translate-x-4 translate-y-4" />
          <img
            src={founder.image}
            alt={founder.name}
            width="1200"
            height="800"
            className="relative rounded-2xl w-full object-cover aspect-[4/5] grayscale-[15%]"
            loading="lazy"
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#0a0a0a]/80 backdrop-blur-md border border-white/10 rounded-xl px-6 py-3 text-center">
            <p className="font-display font-semibold text-white text-sm">{founder.name}</p>
            <p className="text-[#c9a85f] text-xs tracking-wide">{founder.role}</p>
          </div>
        </div>

        <div>
          <p className="font-display tracking-[0.35em] text-[#c9a85f] text-sm uppercase mb-5">The Studio</p>
          <h2 className="font-display font-bold text-white text-4xl md:text-5xl leading-[1.05] uppercase">
            No matter the size of your company, video is your bridge to success
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            Simply Gold Productions is a British Columbia video studio — with teams in the
            Okanagan and Vancouver area — that elevates your message and opens new opportunities.
            Whether it’s a testimonial, product film or brand story, we shape the right
            narrative — and stay by your side until it’s right.
          </p>

          <div className="mt-10">
            <p className="font-display text-sm uppercase tracking-[0.2em] text-[#c9a85f] font-semibold mb-6">Trusted by organizations across Canada</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-7">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-display font-bold text-[#c9a85f] text-2xl md:text-3xl leading-none">{s.value}</p>
                  <p className="text-white/70 text-sm font-medium mt-1.5 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="group inline-flex items-center gap-2 mt-10 text-white font-display font-semibold border-b-2 border-[#c9a85f] pb-1 hover:gap-3 transition-all"
          >
            Start Your Project
            <ArrowUpRight size={18} className="text-[#c9a85f]" />
          </a>
        </div>
      </div>

      {/* What Clients Are Saying */}
      <div id="testimonials" className="mt-20 md:mt-28 scroll-mt-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="font-display tracking-[0.35em] text-[#c9a85f] text-sm uppercase mb-4">Testimonials</p>
          <h3 className="font-display font-bold text-white text-3xl md:text-4xl uppercase leading-[1.05]">
            What Clients Are Saying
          </h3>
        </div>
        <button
          onClick={() => onPlay(clientVideo.youtubeId, clientVideo.title)}
          data-testid="client-video-button"
          className="group relative block w-full max-w-4xl mx-auto rounded-2xl overflow-hidden aspect-video border border-white/10 hover:border-[#c9a85f]/60 transition-colors"
        >
          <img
            src={clientVideo.thumb}
            alt={clientVideo.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#c9a85f]/90 text-black group-hover:scale-110 transition-transform shadow-xl">
              <Play size={24} className="fill-black ml-1" />
            </span>
          </span>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
            <h4 className="font-display font-semibold text-white text-lg md:text-xl uppercase">{clientVideo.title}</h4>
          </div>
        </button>

        <Results />

        <figure className="mt-10 max-w-4xl mx-auto">
          <img
            src="/media/team.webp"
            alt="The Simply Gold Productions team"
            width="2000"
            height="1500"
            loading="lazy"
            className="w-full rounded-2xl border border-white/10 object-cover"
          />
          <figcaption className="mt-6 text-center text-white/70 text-base md:text-lg font-light max-w-2xl mx-auto">
            Meet the team behind the lens — a tight-knit crew of storytellers, shooters and editors
            based in the Okanagan and Vancouver area, dedicated to bringing your vision to life from
            the first call to the final cut.
          </figcaption>
        </figure>
      </div>
    </section>
  );
};

export default About;
